/**
 * Townsend / AMIDS error-response parser.
 *
 * When the backend `request_pn` (or any other AMIDS-backed) API fails, the raw
 * error payload comes back as a stringified Python dict that is extremely
 * user-hostile. It typically looks like:
 *
 *   {'program of eero Accessory Program failed':
 *      b'{"error": "<class '...DependencyException'>",
 *         "reason": {"_Structure__data": {
 *            "message": "[{\"propertyId\":{\"value\":\"REGION\"},
 *                          \"validationFailureList\":[
 *                            {\"message\":\"Definition property value: \\u0027AR\\u0027
 *                               is not in the option set OptionSet(
 *                                 id\\u003dLAB PACKOUT ASSEMBLY REGION,
 *                                 dataType\\u003dSTRING,
 *                                 allowedValues\\u003d[AE, AU, ...],
 *                                 inactiveValues\\u003d[])\",
 *                             \"failureReason\":\"INVALID_VALUE\"}]}]
 *                       (Service: MaterialDefinitionService; Status Code: 422;
 *                        Error Code: ValidationException; ...)"
 *   ...}}
 *   'program of eero accessory program failed': b'...Program was not found in Townsend...'
 *   'program of EERO ACCESSORY PROGRAM failed': b'...Program was not found in Townsend...'
 *   'program of Eero Accessory Program failed': b'...Program was not found in Townsend...'
 *
 * The backend tries multiple case-variants of the program name and only one
 * hits Townsend for real; the other three always come back "Program was not
 * found in Townsend" and are just noise.
 *
 * This module extracts the meaningful ValidationException detail and turns it
 * into an actionable message for the user.
 *
 * The parser is deliberately defensive: any parse failure falls back to
 * returning the raw string so we never regress the user-visible error.
 */

/** Structured Townsend validation failure — one row of the AMIDS response. */
export interface TownsendValidationFailure {
  /** Property that failed validation, e.g. `REGION`. */
  propertyId: string;
  /** The offending value the caller sent, e.g. `AR`. */
  invalidValue?: string;
  /** OptionSet id, e.g. `LAB PACKOUT ASSEMBLY REGION`. */
  optionSetId?: string;
  /** Allowed values from the OptionSet, e.g. `['AE', 'AU', …]`. */
  allowedValues?: string[];
  /** Townsend failure reason, e.g. `INVALID_VALUE`. */
  failureReason?: string;
}

/** Result of parsing a Townsend / AMIDS error payload. */
export interface ParsedTownsendError {
  /**
   * Human-readable message safe to surface directly in a flashbar.
   * Never empty — even when the payload can't be parsed at all, this contains
   * the raw payload string as a last resort.
   */
  userMessage: string;
  /** Structured validation failures, if the payload was a ValidationException. */
  validationFailures?: TownsendValidationFailure[];
  /** True when the payload was a real ValidationException (not "not found in Townsend"). */
  isValidationException: boolean;
  /** True when at least one sub-error was suppressed as noise. */
  hadSuppressedNoise: boolean;
}

/**
 * Strings we consider "noise" — repeated across the 4 case-variant program
 * names AMIDS tries when only one is the correct casing.
 */
const NOISE_MESSAGE_MARKER = 'Program was not found in Townsend';

const VALIDATION_EXCEPTION_MARKER = 'ValidationException';

/**
 * Extract the four candidate error-string values from a Python-repr–style dict
 * that looks like:
 *   `{'program of Foo failed': b'{...}', 'program of foo failed': b'{...}', ...}`
 *
 * We do this with a regex rather than trying to eval the Python literal because
 * the payload contains a chaos of escape sequences that can't be trusted to be
 * JSON-parseable.
 */
const extractPerProgramBlobs = (raw: string): string[] => {
  // Match  b'...'   OR   b"..."   segments (Python bytes literals).
  //
  // Each alternative is written to be *unambiguous*: branch 1 consumes any
  // escape sequence (`\.`), branch 2 consumes any character that is NOT a
  // backslash and NOT the delimiter quote. Every input character therefore
  // has exactly one path through the regex, which prevents catastrophic
  // backtracking on truncated / malformed AMIDS payloads that never reach a
  // closing quote. Using two explicit alternatives (rather than one
  // backreference-based branch) lets a `b'...'` blob still contain literal
  // unescaped `"` characters and vice versa.
  const results: string[] = [];
  const re = /b'((?:\\.|[^\\'])*)'|b"((?:\\.|[^\\"])*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    results.push(m[1] ?? m[2]);
  }
  return results;
};

/**
 * Given a blob that contains a Townsend ValidationException message, extract
 * the structured failures.
 *
 * We parse loosely — the payload contains `\\u003d` sequences that should be
 * `=`, `\\u0027` for `'`, and a smattering of extra backslashes from being
 * JSON-stringified twice. We just look for the tokens we need.
 */
const parseValidationFailures = (blob: string): TownsendValidationFailure[] => {
  const failures: TownsendValidationFailure[] = [];

  // Decode Python/JSON escape sequences we care about.
  const decoded = blob
    .replace(/\\+u0027/g, "'")
    .replace(/\\+u003d/g, '=')
    .replace(/\\+"/g, '"')
    .replace(/\|\|/g, '');

  // Look for repeated `propertyId ... value ... "PROP" ...` groupings.
  // Each validation entry contains a message with:
  //   Definition property value: 'AR' is not in the option set OptionSet(id=LAB PACKOUT ASSEMBLY REGION,
  //     dataType=STRING, allowedValues=[AE, AU, ...], inactiveValues=[...])
  // and a `failureReason: "INVALID_VALUE"`.
  const propertyBlockRe =
    /propertyId["'\s:{}]+value["'\s:]+([A-Z0-9_]+)[\s\S]*?Definition property value:\s*'([^']*)'\s*is not in the option set\s*OptionSet\(\s*id\s*=\s*([^,]+),[\s\S]*?allowedValues\s*=\s*\[([^\]]*)\][\s\S]*?failureReason["'\s:]+([A-Z_]+)/g;

  let m: RegExpExecArray | null;
  while ((m = propertyBlockRe.exec(decoded)) !== null) {
    const [, propertyId, invalidValue, optionSetId, allowedRaw, failureReason] = m;
    failures.push({
      propertyId: propertyId.trim(),
      invalidValue: invalidValue.trim(),
      optionSetId: optionSetId.trim(),
      allowedValues: allowedRaw
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
      failureReason: failureReason.trim(),
    });
  }

  // Fallback: if the strict regex didn't match anything but we clearly saw a
  // ValidationException, capture what fields we can — but ONLY when we truly
  // extracted a `propertyId`. If no property was found, leave `failures` empty
  // so the caller emits its generic Townsend-rejection message instead of
  // leaking a placeholder like "UNKNOWN" into the user-facing string.
  if (failures.length === 0 && decoded.includes(VALIDATION_EXCEPTION_MARKER)) {
    const propMatch = /propertyId["'\s:{}]+value["'\s:]+([A-Z0-9_]+)/.exec(decoded);
    if (propMatch) {
      const valueMatch = /Definition property value:\s*'([^']*)'/.exec(decoded);
      const reasonMatch = /failureReason["'\s:]+([A-Z_]+)/.exec(decoded);
      failures.push({
        propertyId: propMatch[1].trim(),
        invalidValue: valueMatch?.[1]?.trim(),
        failureReason: reasonMatch?.[1]?.trim(),
      });
    }
  }

  return failures;
};

/**
 * Format a validation failure into a single human-readable sentence.
 * Falls back gracefully when fields are missing.
 */
const formatFailure = (f: TownsendValidationFailure): string => {
  const parts: string[] = [];
  const property = f.propertyId || 'value';
  if (f.invalidValue !== undefined) {
    parts.push(`${property} '${f.invalidValue}' is not accepted by Townsend`);
  } else {
    parts.push(`${property} was rejected by Townsend`);
  }
  if (f.failureReason) {
    parts[parts.length - 1] += ` (${f.failureReason})`;
  }
  if (f.allowedValues && f.allowedValues.length > 0) {
    parts.push(`Allowed values: ${f.allowedValues.join(', ')}.`);
  }
  const joined = parts.join('. ');
  return joined.endsWith('.') ? joined : `${joined}.`;
};

/**
 * Parse a raw Townsend / AMIDS error payload into an actionable message.
 *
 * @param raw The stringified error body coming back from `request_pn` (or any
 *            AMIDS-backed call). May be any string — including one that does
 *            not match the expected Townsend shape.
 * @returns A structured parse result. `userMessage` is always populated.
 */
export const parseTownsendError = (raw: string | null | undefined): ParsedTownsendError => {
  const safeRaw = (raw ?? '').toString();

  // No content at all
  if (!safeRaw.trim()) {
    return {
      userMessage: 'Failed to request part number.',
      isValidationException: false,
      hadSuppressedNoise: false,
    };
  }

  // Grab the per-program-name blobs. If none, treat the entire string as one blob.
  const blobs = extractPerProgramBlobs(safeRaw);
  const candidateBlobs = blobs.length > 0 ? blobs : [safeRaw];

  // Identify noise blobs and real ones.
  const validationBlobs = candidateBlobs.filter((b) => b.includes(VALIDATION_EXCEPTION_MARKER));
  const noiseBlobs = candidateBlobs.filter((b) => b.includes(NOISE_MESSAGE_MARKER));
  const hadSuppressedNoise = noiseBlobs.length > 0 && validationBlobs.length > 0;

  // Prefer ValidationException — that's the real signal.
  if (validationBlobs.length > 0) {
    const failures = validationBlobs.flatMap(parseValidationFailures);
    const userMessage =
      failures.length > 0
        ? `Townsend rejected the part number request. ${failures.map(formatFailure).join(' ')}`
        : // Payload said ValidationException but we couldn't drill into the details.
          "Townsend rejected the part number request due to a validation error. Please check the SKU's regions / marketplaces and try again.";

    return {
      userMessage,
      validationFailures: failures.length > 0 ? failures : undefined,
      isValidationException: true,
      hadSuppressedNoise,
    };
  }

  // If we only ever saw "not found in Townsend" noise, surface a single
  // deduped message instead of repeating it 4x.
  if (noiseBlobs.length > 0) {
    return {
      userMessage:
        'Program was not found in Townsend. Verify the program name and its Townsend configuration, then retry.',
      isValidationException: false,
      hadSuppressedNoise: noiseBlobs.length > 1,
    };
  }

  // Unknown shape — fall back to the raw string so we never lose information.
  return {
    userMessage: safeRaw,
    isValidationException: false,
    hadSuppressedNoise: false,
  };
};
